import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MapPin } from "lucide-react";
import { BookLink, PHONE_TEL, PHONE_DISPLAY, ADDRESS_LINE, ADDRESS_CITY } from "./booking";

const LINES = [
  { text: "FRESH CUTS.", cls: "text-foreground" },
  { text: "CLEAN FADES.", cls: "text-primary" },
];

const HeroBackdrop = ({ y }) => (
  <div className="absolute inset-0 overflow-hidden bg-brand-obsidian" aria-hidden="true">
    <motion.div style={{ y }} className="absolute inset-0 flex items-center">
      <div className="flex w-max animate-marquee whitespace-nowrap">
        {[...Array(4)].map((_, i) => (
          <span
            key={i}
            className="font-display uppercase leading-none text-transparent text-[42vw] lg:text-[26rem] px-10 [-webkit-text-stroke:1.5px_rgba(232,230,225,0.06)]"
          >
            Faded
          </span>
        ))}
      </div>
    </motion.div>
    <div className="absolute -top-1/4 -left-1/4 w-2/3 aspect-square rounded-full bg-primary/10 blur-[100px]" />
    <div className="absolute -bottom-1/3 -right-1/4 w-2/3 aspect-square rounded-full bg-accent/10 blur-[100px]" />
    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/70" />
  </div>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden flex flex-col justify-end"
    >
      <HeroBackdrop y={y} />

      <div className="relative px-5 lg:px-12 pb-24 pt-36 w-full">
        <h1 className="font-display uppercase leading-[0.85] tracking-tight text-[19vw] sm:text-[15vw] lg:text-[10.5rem]">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.76, 0, 0.24, 1] }}
                className={`block ${line.cls}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-md text-sm sm:text-lg leading-relaxed text-muted-foreground font-light"
          >
            Premium haircuts, fades, beard grooming, and classic barber services in Springfield, VA.
            Walk-ins welcome — booking online takes seconds.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <BookLink testid="hero-book-button" className="btn-primary">
              Book an Appointment
            </BookLink>
            <a href={PHONE_TEL} data-testid="hero-call-button" className="btn-outline">
              <Phone size={16} aria-hidden="true" /> Call {PHONE_DISPLAY}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-14 flex items-center justify-between border-t border-foreground/10 pt-5"
        >
          <p className="font-mono text-[11px] sm:text-xs tracking-[0.2em] uppercase text-foreground/50">
            {ADDRESS_LINE} — {ADDRESS_CITY}
          </p>
          <p className="font-mono text-[11px] sm:text-xs tracking-[0.2em] uppercase text-foreground/50 hidden sm:flex items-center gap-3">
            Scroll
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
              className="block w-px h-6 bg-primary"
              aria-hidden="true"
            />
          </p>
        </motion.div>
      </div>
    </section>
  );
}
